import type { CharacterLook } from '../state/types';
import {
  BOOZE_SLOT,
  BRENDA_SLOT,
  GAS_SLOT,
  HANK_SLOT,
  LAUNDRY_SLOT,
  PAWN_SLOT,
  roomSpot,
  WANDA_SLOT,
} from '../world/rooms';
import type { NpcDef } from './npcs';
import { needMoney, onceToday, type Choice } from './types';

/**
 * The folks behind the counters on the strip, and the regulars at the laundromat.
 * Their homes are inside the shops (rooms out past the east edge of the map).
 */

/** Room-local spots for each shop's clerk (kept here so content doesn't import three.js). */
export const CLERK_SPOTS = {
  gas: { x: -4.6, z: -3.0 },
  pawn: { x: 0, z: -3.0 },
  booze: { x: 5.0, z: -0.4 },
  laundry: { x: 5.6, z: -3.4 },
  pickles: { x: -2.1, z: 3.55 },
  brenda: { x: 2.55, z: -0.35 },
  cooter: { x: -0.4, z: -1.0 },
  wanda: { x: 0.6, z: -1.25 },
  hank: { x: 2.2, z: -0.5 },
} as const;

const look = (l: Partial<CharacterLook> & Pick<CharacterLook, 'skin' | 'hair'>): CharacterLook => ({
  hairColor: '#5b3a1e',
  beard: 'none',
  top: 'tee',
  topColor: '#dddddd',
  bottom: 'jeans',
  bottomColor: '#3b5b8c',
  shoes: 'sneakers',
  shoeColor: '#eeeeee',
  hat: 'none',
  hatColor: '#333333',
  belly: 0.3,
  height: 1,
  ...l,
});

export const TOWNIES: readonly NpcDef[] = [
  {
    id: 'raylene',
    name: 'Raylene',
    title: 'Gas-N-Grub, Behind the Glass',
    voice: 300,
    home: roomSpot(GAS_SLOT, CLERK_SPOTS.gas),
    wander: 0,
    facing: 0,
    hours: [0, 24],
    look: look({
      body: 'woman',
      skin: '#e8b892',
      hair: 'teased',
      hairColor: '#8a2b2b',
      top: 'tee',
      topColor: '#c62828',
      bottom: 'jeans',
      bottomColor: '#2b3a5a',
      belly: 0.35,
      height: 0.96,
    }),
    lines: () => [
      "The glass is bulletproof, sugar. Ask me how I know. Actually don't.",
      'Roller dogs are fresh. Fresh is a strong word. They are hot.',
      'Pump four is broken. Pump four has always been broken. Pump four is a lifestyle.',
      "Somebody won fifty bucks on a scratcher yesterday. They spent it here by lunch. That's the circle of life.",
    ],
    choices: ({ ui }) => [
      { label: 'Buy something', run: () => ui.openShop('gas') },
      {
        label: "Ask what's good today",
        run: ({ rng }) =>
          rng.pick([
            "'The chicken bucket's from this morning. This morning-ish. Get the bucket.'",
            "'Don't get the sushi. I'm legally required to sell it. I'm not legally required to recommend it.'",
            "'Rocket Fuel just came in. Cleetus drank three and saw sounds. Your call.'",
          ]),
      },
    ],
  },
  {
    id: 'lucky',
    name: 'Lucky',
    title: "Lucky's Pawn & Payday",
    voice: 160,
    home: roomSpot(PAWN_SLOT, CLERK_SPOTS.pawn),
    wander: 0,
    facing: 0,
    hours: [9, 21],
    look: look({
      skin: '#d9a07a',
      hair: 'bald',
      hairColor: '#3a2a1a',
      beard: 'goatee',
      top: 'tracksuit',
      topColor: '#1b1b1b',
      bottom: 'sweats',
      bottomColor: '#1b1b1b',
      shoes: 'sneakers',
      shoeColor: '#f4f4f0',
      shades: true,
      belly: 0.6,
      height: 1.04,
    }),
    lines: () => [
      'Everything in here has a story. Most of them end in a divorce.',
      "I'll give you forty for it. I don't even know what it is. Forty.",
      'The bars are for the merchandise. The shotgun is for the customers. Kidding! Mostly.',
      'That guitar belonged to a man who almost opened for Travis Tritt. Almost is doing a lot of work there.',
    ],
    choices: ({ ui }) => [
      { label: 'See what you got', run: () => ui.openShop('pawn') },
      {
        label: 'Ask about the gold chains',
        run: () =>
          "'Fourteen karat. Some of it. The part that matters is the part people see.' He taps his own chain. It leaves green on his finger.",
      },
    ],
  },
  {
    id: 'dottie',
    name: 'Dottie',
    title: 'Booze Barn, Register',
    voice: 360,
    home: roomSpot(BOOZE_SLOT, CLERK_SPOTS.booze),
    wander: 0,
    facing: -Math.PI / 2,
    hours: [8, 26],
    look: look({
      body: 'woman',
      skin: '#f1c6a8',
      hair: 'mullet',
      hairColor: '#c9c9c9',
      top: 'flannel',
      topColor: '#2f6b3a',
      bottom: 'jeans',
      bottomColor: '#3b5b8c',
      shoes: 'boots',
      shoeColor: '#5a3a1e',
      belly: 0.45,
      height: 0.94,
    }),
    lines: () => [
      'I card everybody. I carded my own mama. She was flattered. She was 71.',
      'The chicken wire keeps the liquor in and the customers out. Mostly the customers.',
      "Strawberry Hill's on sale. It's always on sale. That should tell you something.",
      'We close at 2. At 1:59 I become a very different woman.',
    ],
    choices: ({ ui }) => [
      { label: 'Ring me up', run: () => ui.openShop('booze') },
      {
        label: 'Ask for a recommendation',
        run: ({ rng }) =>
          rng.pick([
            "'Thirty-rack. You're gonna buy the thirty-rack anyway. Skip the part where you pretend you won't.'",
            "'Fire Bawl. Three of 'em. Then go home. Do not go to the Rusty Spur. You will go to the Rusty Spur.'",
          ]),
      },
    ],
  },
  {
    id: 'opal',
    name: 'Miss Opal',
    title: 'Suds-N-Duds, Attendant',
    voice: 400,
    home: roomSpot(LAUNDRY_SLOT, CLERK_SPOTS.laundry),
    wander: 0,
    facing: 0,
    hours: [6, 22],
    look: look({
      body: 'woman',
      skin: '#8a5a3c',
      hair: 'teased',
      hairColor: '#d9d9d9',
      top: 'tee',
      topColor: '#7a9cc0',
      bottom: 'sweats',
      bottomColor: '#4a4a6a',
      shoes: 'slippers',
      shoeColor: '#f3d9e6',
      belly: 0.4,
      height: 0.92,
    }),
    lines: () => [
      'Machine six eats quarters. Machine six has eaten forty dollars of mine. Machine six and I are at war.',
      "No dyeing in the machines. I don't care what color your truck is.",
      "I've found three wedding rings, a set of dentures and a live snake in the lost and found. The snake's name was Gary.",
      'You smell like a campfire and a bad decision. Wash and fold is eight dollars.',
    ],
    choices: ({ store }) => [
      {
        label: 'Wash & fold service',
        hint: '-$8, fresh all day',
        requires: needMoney(8),
        run: ({ store: st }) => {
          st.spend(8);
          st.setFlag('fresh', st.day);
          st.incr('laundry');
          return {
            text: "Miss Opal takes your bag with two fingers and a look. An hour later it comes back folded so sharp you could cut cheese. You smell like a mountain breeze that's been to church. (Fresh: +1 Clout on everything today.)",
            clout: 2,
            minutes: 60,
          };
        },
      },
      ...(store.flag('fresh') === store.day
        ? []
        : [
            {
              label: 'Ask why you should bother',
              run: () =>
                "'Fresh clothes, baby. People treat you different. +1 Clout on everything you do all day. That's science. I read it on a detergent box.'",
            },
          ]),
    ],
  },
  {
    id: 'pickles',
    name: 'Pickles',
    title: 'Suds-N-Duds Regular',
    voice: 190,
    home: roomSpot(LAUNDRY_SLOT, CLERK_SPOTS.pickles),
    wander: 0,
    sitting: true,
    facing: Math.PI,
    hours: [9, 23],
    look: look({
      skin: '#f0c8a8',
      hair: 'rattail',
      hairColor: '#8a6a3a',
      beard: 'stache',
      top: 'nascar',
      topColor: '#f4f4f0',
      bottom: 'jorts',
      bottomColor: '#6b8fb3',
      shoes: 'crocs',
      shoeColor: '#2f8f3a',
      belly: 0.7,
      height: 0.98,
    }),
    lines: () => [
      "I've been here since 2011. My laundry's been done since 2011. It's the company.",
      'Did I ever tell you about the time I almost married a weather girl? Channel 6. Almost.',
      'Dryer four runs hot. Dryer four will shrink your jeans and your self-esteem.',
      "They call me Pickles because of a thing that happened at a county fair. I'm not at liberty.",
    ],
    choices: () => [
      {
        label: 'Help Pickles fold his laundry',
        hint: '+$4',
        requires: onceToday('pickles_fold', 'Pickles is down to socks. He says socks are a solo activity.'),
        run: ({ store: st }) => {
          st.markToday('pickles_fold');
          return {
            text: 'Forty minutes of folding. Pickles owns eleven identical NASCAR shirts and one sock that is clearly not his. He tips you four dollars in quarters, warm from the dryer.',
            money: 4,
            clout: 1,
            minutes: 40,
          };
        },
      },
      {
        label: 'Ask about the county fair',
        run: ({ rng }) =>
          rng.pick([
            "'There was a pickle-eating contest. I won. Then there was a second contest nobody told me about. I also won that one. I was banned from Tri-County.'",
            "Pickles stares into dryer four for a long time. 'Some things a man carries alone.' He doesn't elaborate. You don't push.",
          ]),
      },
    ],
  },
  {
    id: 'brenda',
    name: 'Brenda',
    title: "Brenda's Beauty Barn (Lot 12)",
    voice: 340,
    home: roomSpot(BRENDA_SLOT, CLERK_SPOTS.brenda),
    wander: 0,
    facing: -Math.PI / 2,
    hours: [9, 19],
    look: look({
      body: 'woman',
      skin: '#f1c6a8',
      hair: 'teased',
      hairColor: '#ffd700',
      top: 'leopard',
      topColor: '#d9a441',
      bottom: 'sweats',
      bottomColor: '#ff6fa8',
      shoes: 'slippers',
      shoeColor: '#ff6fa8',
      belly: 0.35,
      height: 0.97,
    }),
    lines: () => [
      "Sit down, baby. Whatever's going on up there, I can fix it. Or make it a mullet. Same thing.",
      "I don't need a license. I need a chair and a dream. I have both.",
      "Everything said in this chair stays in this chair. Unless it's good. Then it goes on FaceSpace.",
      "I did Darlene's hair for her wedding. Both weddings. One of 'em was to the same man.",
    ],
    choices: ({ rng }) => [
      {
        label: 'Get the gossip',
        requires: onceToday(
          'brenda_gossip',
          "Brenda's all talked out. 'Come back tomorrow, the well refills overnight.'",
        ),
        run: ({ store: st }) => {
          st.markToday('brenda_gossip');
          return {
            text: rng.pick([
              "'Junkyard Jerry pays more for copper than cans. A LOT more. The condemned trailers in the Back Forty are full of it. You didn't hear it from me.'",
              "'Gus at the gun range pays a prize to the best shot every day. Fifteen points. Hold your breath, shoot the running possum, it's worth three.'",
              "'Miss Opal at the laundromat? Wash your clothes there and people treat you nicer all day. That's not gossip, that's science.'",
              "'Madame Wanda next door is a fraud. Except when she isn't. Get a reading before you buy a scratcher. Just saying.'",
              "'Hank on Lot 15 swears he's seen a spaceship over the Back Forty. Late. Real late. Usually when he's out there in that foil hat.'",
              "'Tammy Lynn's essential oils gave Cousin Dale a rash shaped like Florida. He's selling it as a tattoo now.'",
            ]),
            clout: 1,
          };
        },
      },
    ],
  },
  {
    id: 'wanda',
    name: 'Madame Wanda',
    title: 'Psychic Readings (Lot 14)',
    voice: 250,
    home: roomSpot(WANDA_SLOT, CLERK_SPOTS.wanda),
    wander: 0,
    sitting: true,
    facing: 0,
    hours: [12, 26],
    look: look({
      body: 'woman',
      skin: '#c88a64',
      hair: 'teased',
      hairColor: '#4a1f6a',
      top: 'flannel',
      topColor: '#6b1f3a',
      bottom: 'sweats',
      bottomColor: '#3a1f4a',
      shoes: 'slippers',
      shoeColor: '#c9a23a',
      belly: 0.4,
      height: 0.93,
    }),
    lines: () => [
      'I sensed you were coming. Also I heard your Crocs on the steps.',
      'The spirits are restless tonight. That, or it is the propane tank.',
      "Your aura is... brown. That's not bad. That's just very Lucky Acres.",
      "I see a tall, dark stranger in your future. Oh, no, that's Big Tiny. He's behind you.",
    ],
    choices: () => [
      {
        label: 'Ask the spirits about the lights in the sky',
        run: () =>
          "Wanda's eyes roll back. 'Late. Past midnight. Out where the crick runs and the dogs won't go. They come for the ones who wear the foil. Or the ones who drink the shine. Mostly the shine.'",
      },
    ],
  },
  {
    id: 'hank',
    name: 'Hank',
    title: 'Lot 15. They Are Watching.',
    voice: 140,
    home: roomSpot(HANK_SLOT, CLERK_SPOTS.hank),
    wander: 0,
    facing: 0,
    hours: [0, 24],
    look: look({
      skin: '#e0b090',
      hair: 'buzz',
      hairColor: '#7a6a5a',
      beard: 'full',
      top: 'camo',
      topColor: '#5a6b3a',
      bottom: 'jeans',
      bottomColor: '#3a3a2e',
      shoes: 'boots',
      shoeColor: '#3a2a1a',
      hat: 'trucker',
      hatColor: '#c9ced3',
      belly: 0.5,
      height: 1.02,
    }),
    lines: () => [
      "Shut the door. Faster. They can see through it when it's open, that's basic science.",
      "The water tower isn't a water tower. Ask yourself why it never runs out of water.",
      'I have enough beans in here to last eleven years. Or one bad week. Depends on the week.',
      'Apex Capital, the government, the lizard people. Same people. Different vests.',
    ],
    choices: ({ store }) => [
      {
        label: 'Buy a tinfoil hat',
        hint: '-$5',
        requires: (ctx) =>
          ctx.store.has('foilHat')
            ? 'You already have one. Hank says one is plenty. Two is "suspicious."'
            : needMoney(5)(ctx),
        run: ({ store: st }) => {
          st.spend(5);
          st.give('foilHat', 1);
          return "Hank fits it to your skull with real care. 'Three layers of heavy-duty Reynolds. Blocks the signals. Or, now that I think about it, it might boost them. Wear it out in the Back Forty late at night and let me know which.'";
        },
      },
      ...(Number(store.flag('ufo_unreported') ?? 0) > 0
        ? ([
            {
              label: 'Tell Hank what happened to you',
              hint: '+$50',
              run: ({ store: st }) => {
                st.setFlag('ufo_unreported', 0);
                st.unlock('believer');
                return {
                  text: "Hank goes white, then red, then white again. He records the whole thing on a cassette, labels it EVIDENCE, and presses fifty dollars into your hand. 'They took you. They TOOK you. Do you know what this means?' You do not. Neither does he. He's thrilled.",
                  money: 50,
                  clout: 8,
                };
              },
            },
          ] satisfies Choice[])
        : []),
      ...(store.has('alienGizmo')
        ? ([
            {
              label: 'Show Hank the glowing doohickey',
              hint: '+$150',
              run: ({ store: st }) => {
                st.take('alienGizmo');
                return {
                  text: "Hank holds it like a newborn. 'Not of this earth. Not of this COUNTY.' He empties a coffee can of emergency money into your hands: $150. He's going to sleep with it under his pillow.",
                  money: 150,
                  clout: 5,
                };
              },
            },
          ] satisfies Choice[])
        : []),
      {
        label: 'Help Hank restock the bunker',
        hint: '+$10',
        requires: onceToday(
          'hank_stock',
          "Bunker's stocked. Hank's doing inventory. He'll be at it till Thursday.",
        ),
        run: ({ store: st }) => {
          st.markToday('hank_stock');
          return {
            text: 'You carry in forty cans of beans, a case of Spam and a crate labeled "BATTERIES (D) (DO NOT ASK)". Hank pays you ten bucks and makes you swear on a can of beans you saw nothing.',
            money: 10,
            stats: { energy: -8 },
            minutes: 45,
          };
        },
      },
    ],
  },
];
